import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Database,
  Download,
  Trash2,
  Clock,
  HardDrive,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Loader2,
  Calendar,
  FileArchive
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useLanguage } from '../../contexts/LanguageContext';
import { t } from '../../lib/translations';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BackupManagementPage = () => {
  const [loading, setLoading] = useState(true);
  const { lang } = useLanguage();
  const [backups, setBackups] = useState([]);
  const [lastBackup, setLastBackup] = useState(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    fetchBackups();
  }, []);

  const fetchBackups = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/admin/backups`);
      setBackups(res.data.backups);
      setLastBackup(res.data.last_backup);
    } catch (error) {
      console.error('Error fetching backups:', error);
    } finally {
      setLoading(false);
    }
  };

  const createBackup = async () => {
    try {
      setCreating(true);
      await axios.post(`${API_URL}/admin/backup`);
      await fetchBackups();
    } catch (error) {
      console.error('Error creating backup:', error);
      alert('Erreur lors de la création de la sauvegarde');
    } finally {
      setCreating(false);
    }
  };

  const deleteBackup = async (backupId) => {
    if (!confirm('Supprimer cette sauvegarde ?')) return;
    
    try {
      setDeleting(backupId);
      await axios.delete(`${API_URL}/admin/backups/${backupId}`);
      await fetchBackups();
    } catch (error) {
      console.error('Error deleting backup:', error);
    } finally {
      setDeleting(null);
    }
  };

  const downloadBackup = (backupId) => {
    window.open(`${API_URL}/admin/backups/${backupId}/download`, '_blank');
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="backup-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900">
            Sauvegardes de la base de données
          </h1>
          <p className="text-gray-500">Gérez les sauvegardes automatiques et manuelles</p>
        </div>
        
        <Button onClick={createBackup} disabled={creating}>
          {creating ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Database className="w-4 h-4 mr-2" />
          )}
          Créer une sauvegarde
        </Button>
      </div>

      {/* Status Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <FileArchive className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{backups.length}</p>
                <p className="text-sm text-gray-500">Sauvegardes totales</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center">
                <Clock className="w-6 h-6 text-secondary" />
              </div>
              <div>
                <p className="text-lg font-bold">
                  {lastBackup 
                    ? formatDistanceToNow(new Date(lastBackup), { addSuffix: true, locale: fr })
                    : 'Jamais'
                  }
                </p>
                <p className="text-sm text-gray-500">Dernière sauvegarde</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                <HardDrive className="w-6 h-6 text-accent" />
              </div>
              <div>
                <p className="text-lg font-bold">
                  {formatBytes(backups.reduce((sum, b) => sum + (b.size_bytes || 0), 0))}
                </p>
                <p className="text-sm text-gray-500">Espace utilisé</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Card */}
      <Card className="border-0 shadow-sm bg-blue-50 border-l-4 border-l-blue-500">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5" />
          <div>
            <p className="font-medium text-blue-900">Sauvegardes automatiques</p>
            <p className="text-sm text-blue-700">
              Les sauvegardes automatiques sont effectuées quotidiennement. Les 7 dernières sauvegardes automatiques sont conservées.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Backups List */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading">Historique des sauvegardes</CardTitle>
        </CardHeader>
        <CardContent>
          {backups.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              <Database className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Aucune sauvegarde disponible</p>
              <p className="text-sm">Créez votre première sauvegarde</p>
            </div>
          ) : (
            <div className="space-y-3">
              {backups.map(backup => (
                <div 
                  key={backup.id}
                  className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    backup.status === 'success' ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    {backup.status === 'success' ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900 truncate">{backup.filename}</p>
                      <Badge variant="outline" className={backup.type === 'scheduled' ? 'bg-blue-50' : 'bg-purple-50'}>
                        {backup.type === 'scheduled' ? 'Auto' : 'Manuel'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {format(new Date(backup.created_at), 'dd MMM yyyy HH:mm', { locale: fr })}
                      </span>
                      <span className="flex items-center gap-1">
                        <HardDrive className="w-3 h-3" />
                        {formatBytes(backup.size_bytes)}
                      </span>
                      <span>{backup.collections_count} collections</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => downloadBackup(backup.id)}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => deleteBackup(backup.id)}
                      disabled={deleting === backup.id}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    >
                      {deleting === backup.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BackupManagementPage;
